import { useEffect, useMemo, useState } from 'react';
import type { LandingPageUrls } from '../../landing-page-editor/landing-page-editor';

/**
 * Preloads every breakpoint's HTML up front so switching widths/artboards
 * only swaps already-fetched strings — no network requests during resize.
 * NEW-origin pages collapse to a single URL (responsive doc); EXISTING-origin
 * captures are one document per dimension key.
 */
export function usePreloadedPageHtml(landingPageUrls?: LandingPageUrls | null) {
  const [htmlByUrl, setHtmlByUrl] = useState<Record<string, string>>({});
  const [loadedSignature, setLoadedSignature] = useState<string | null>(null);

  // The unique URLs to preload, and a stable signature for them so we can
  // tell when a given set has finished loading.
  const urls = useMemo(
    () => (landingPageUrls ? Array.from(new Set(Object.values(landingPageUrls))) : []),
    [landingPageUrls]
  );
  const signature = urls.join('\n');

  useEffect(() => {
    if (urls.length === 0) return;

    let cancelled = false;

    Promise.all(
      urls.map(async (url) => {
        try {
          const response = await fetch(url);
          return [url, await response.text()] as const;
        } catch (error) {
          console.error('Failed to preload page HTML:', url, error);
          return null;
        }
      })
    ).then((entries) => {
      if (cancelled) return;
      setHtmlByUrl(
        Object.fromEntries(
          entries.filter((entry): entry is readonly [string, string] => entry !== null)
        )
      );
      setLoadedSignature(signature);
    });

    return () => {
      cancelled = true;
    };
  }, [urls, signature]);

  return {
    htmlByUrl,
    isLoading: urls.length > 0 && loadedSignature !== signature,
  };
}
